import type { AppletsBySessionCodeQuery } from '@innovamat/glow-api-client';
import { useUnblockedApplets } from '@innovamat/glow-api-client';
import { useQueryParams } from '@innovamat/hooks';
import type { Region } from '@innovamat/localization';
import { isJourneyClassroom as checkIsJourneyClassroom } from '../../classrooms-management/modules/classroom';
import { useCurrentNavigationValues } from '../../contents';
import { useJourneyUnblockedApplets } from '../../contents/hooks/use-journey-unblocked-applets';
import type { ContentResource } from '../../contents/types/page';
import { useSessionDone } from './session-done-provider';

type useGetAppletsReturn = {
  data: AppletsBySessionCodeQuery['appletsBySessionCode'] | undefined;
  isError: boolean;
  isPending: boolean;
};

const availableRegions: Region[] = ['ES', 'IT', 'MX', 'CO-B', 'EC-SI', 'US'];

export const askForApplets = (
  resource: ContentResource,
  region: string
): boolean => {
  if (!resource || !resource.code) return false;
  const sessionCode = resource.code;
  //TODO:WHY?? Add link to the documentation,
  const isLaboratory =
    sessionCode.includes('LAB') || sessionCode.startsWith('L');
  const replacedRegion = region?.replace('-LIT', '');
  return availableRegions.includes(replacedRegion as Region) || isLaboratory;
};

export function useGetApplets(): useGetAppletsReturn {
  const { currentClassroom } = useCurrentNavigationValues();
  const isJourneyClassroom = checkIsJourneyClassroom(currentClassroom);

  const { courseId, courseOrder, currentSession, locale, region } =
    useSessionDone();

  const urlRegion = useQueryParams().get('region');
  const urlLanguage = useQueryParams().get('language');

  const journeyUnblockedApplets = useJourneyUnblockedApplets({
    sessionCode: currentSession?.code || '',
    language: urlLanguage || locale,
    region: urlRegion || region,
    enabled: isJourneyClassroom,
  });

  const appletsQuery = useUnblockedApplets({
    language: urlLanguage || locale,
    courseId: courseId,
    courseOrder: courseOrder,
    sessionCode: currentSession?.code!,
    region: urlRegion || region,
    enabled: !isJourneyClassroom,
  });

  const isRealError =
    appletsQuery.isError &&
    appletsQuery.data?.appletsBySessionCode?.length === 0;

  const isJourneyRealError =
    journeyUnblockedApplets.isError &&
    journeyUnblockedApplets.applets?.length === 0;

  if (isJourneyClassroom) {
    return {
      data: journeyUnblockedApplets.applets,
      isError: isJourneyRealError,
      isPending: journeyUnblockedApplets.isPending,
    };
  }

  return {
    data: appletsQuery.data?.appletsBySessionCode?.filter((applet) =>
      Boolean(applet?.data)
    ),
    isError: isRealError,
    isPending: appletsQuery.isPending,
  };
}
